import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import SubAdminServices from "../../../services/SubAdminServices";

const baseSchema = {
  name: Yup.string().required("Full name is required"),
  mobile: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  username: Yup.string()
    .min(3, "User name must be at least 3 characters long")
    .required("User name is required"),
  nike_name: Yup.string().required("Nick name is required"),
  location: Yup.string().required("Location is required"),
};

const addSchema = Yup.object().shape({
  ...baseSchema,
  password: Yup.string()
    .min(3, "Password must be at least 3 characters long")
    .required("Password is required"),
});
const editSchema = Yup.object().shape(baseSchema);

/** One modal for add and edit: `editData` present means edit. */
const SubAdminFormModal = ({ show, onClose, editData, onSaved }) => {
  const api = new SubAdminServices();
  const isEdit = Boolean(editData);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = {
    name: editData?.name ?? "",
    mobile: editData?.mobile ?? "",
    username: editData?.username ?? "",
    password: "",
    nike_name: editData?.nike_name ?? "",
    location: editData?.location ?? "",
  };

  const field = (name, label, placeholder, extra = {}) => (
    <div className="form-group mb-2">
      <label htmlFor={name} className="form-label mb-0">
        {label} <span className="required-asterisk">*</span>
      </label>
      <div className="input-wrapper">
        <Field id={name} name={name} placeholder={placeholder} className="form-input" {...extra} />
      </div>
      <ErrorMessage name={name}>
        {(msg) => <div className="admin_error-text">{msg}</div>}
      </ErrorMessage>
    </div>
  );

  return (
    <Modal show={show} onHide={onClose} backdrop="static" className="custom-modal">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={isEdit ? editSchema : addSchema}
        onSubmit={async (values, { setSubmitting }) => {
          setIsLoading(true);
          const payload = {
            name: values.name,
            mobile: values.mobile,
            username: values.username,
            nike_name: values.nike_name,
            location: values.location,
          };
          try {
            const response = isEdit
              ? await api.update({ ...payload, id: editData.id })
              : await api.add({ ...payload, password: values.password });
            if (response.isSuccess) {
              toast.success(response.message);
              onClose();
              onSaved();
            } else {
              toast.error(response.message);
            }
          } catch (error) {
            toast.dismiss();
            toast.error(error?.response?.data?.message || "Could not save sub admin");
          } finally {
            setIsLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form className="addAdmin-form">
            <div className="modal-header pb-0 pt-2">
              <h5 className="modal-title">{isEdit ? "Edit Sub Admin" : "Add Sub Admin"}</h5>
              <button type="button" className="btn" onClick={onClose}>
                <RiCloseLargeFill />
              </button>
            </div>
            <Modal.Body className="modal-body-scroll">
              {field("name", "Full Name", "Enter full name")}
              {field("mobile", "Mobile Number", "10 digit mobile number", {
                onChange: (e) => setFieldValue("mobile", e.target.value.replace(/[^0-9]/g, "")),
                maxLength: 10,
              })}
              {field("username", "User Name", "Login user name", { autoComplete: "off" })}

              {!isEdit && (
                <div className="form-group mb-2">
                  <label htmlFor="password" className="form-label mb-0">
                    Password <span className="required-asterisk">*</span>
                  </label>
                  <div className="input-wrapper">
                    <Field
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      placeholder="Login password"
                      className="form-input"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex="-1"
                    >
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  <ErrorMessage name="password">
                    {(msg) => <div className="admin_error-text">{msg}</div>}
                  </ErrorMessage>
                </div>
              )}

              {field("nike_name", "Nick Name", "Short display name")}
              {field("location", "Location", "City / area")}

              <div className="d-flex align-items-center justify-content-end">
                <button type="submit" className="add_admin-btn" disabled={isSubmitting || isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      Saving...
                    </>
                  ) : isEdit ? (
                    "Update"
                  ) : (
                    "Save Now"
                  )}
                </button>
              </div>
            </Modal.Body>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default SubAdminFormModal;
